import { useState, useEffect, useRef } from 'react';

/**
 * Hook to track which section is currently active in the viewport
 * Uses IntersectionObserver with fallback to scroll listener
 * 
 * @param {Array} sections - Array of section objects with { id: string }
 * @param {number} offset - Offset in pixels for sticky headers
 * @returns {Object} - { activeSection: string|null, isProgrammaticScrollRef: ref }
 */
export const useActiveSection = (sections = [], offset = 0) => {
  const [activeSection, setActiveSection] = useState(null);
  const isProgrammaticScrollRef = useRef(false);
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    if (!sections || sections.length === 0) return;

    // Check if IntersectionObserver is supported
    const hasIntersectionObserver = 'IntersectionObserver' in window;

    if (hasIntersectionObserver) {
      const rootMarginTop = Math.max(0, offset);
      const observerOptions = {
        root: null,
        rootMargin: `-${rootMarginTop}px 0px -60% 0px`,
        threshold: [0, 0.1, 0.5, 1]
      };

      const sectionPositions = new Map();

      const observers = sections.map((section) => {
        const element = document.getElementById(section.id);
        if (!element) return null;

        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            // Skip updates during programmatic scroll to prevent jump-back
            if (isProgrammaticScrollRef.current) return;
            
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
              // Track position of visible sections
              sectionPositions.set(section.id, entry.boundingClientRect.top);
            } else {
              sectionPositions.delete(section.id);
            }
            
            // Debounce updates to prevent flickering
            if (updateTimeoutRef.current) {
              clearTimeout(updateTimeoutRef.current);
            }
            
            updateTimeoutRef.current = setTimeout(() => {
              // Get the section closest to the top of the viewport
              if (sectionPositions.size > 0) {
                let closestSection = null;
                let minDistance = Infinity;
                
                sectionPositions.forEach((top, sectionId) => {
                  const distance = Math.abs(top);
                  if (distance < minDistance) {
                    minDistance = distance;
                    closestSection = sectionId;
                  }
                });
                
                if (closestSection) {
                  setActiveSection(closestSection);
                }
              }
            }, 100);
          });
        }, observerOptions);

        observer.observe(element);
        return observer;
      }).filter(Boolean);

      return () => {
        observers.forEach(observer => observer.disconnect());
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
      };
    } else {
      // Fallback: scroll listener for older browsers
      let ticking = false;
      
      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            // Skip updates during programmatic scroll to prevent jump-back
            if (isProgrammaticScrollRef.current) {
              ticking = false;
              return;
            }
            
            const scrollPosition = window.scrollY + offset + 100;
            
            // Check sections from bottom to top
            for (let i = sections.length - 1; i >= 0; i--) {
              const element = document.getElementById(sections[i].id);
              if (element) {
                const elementTop = element.offsetTop;
                if (scrollPosition >= elementTop) {
                  setActiveSection(sections[i].id);
                  break;
                }
              }
            }
            
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll(); // Initial check
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [sections, offset]);

  return { activeSection, isProgrammaticScrollRef };
};

export default useActiveSection;

